import type { LoginRedirectMode } from "@/lib/settings/basic-page";
import { normalizeInternalUrl } from "@/lib/site/trailing-slash";

export const MEMBER_LOGIN_PATH = "/member/login/";

export const MEMBER_REGISTER_PATH = "/member/register/";
export const MEMBER_RESET_PASSWORD_PATH = "/member/reset-password/";

const AUTH_ENTRY_PATHS = new Set([MEMBER_LOGIN_PATH, MEMBER_REGISTER_PATH, MEMBER_RESET_PASSWORD_PATH]);

// 登录/注册页自身不能作为登录后的落点

function isAuthEntry(target: string) {
  const pathname = target.split(/[?#]/)[0] ?? "/";
  const normalized = `${pathname.replace(/\/+$/, "")}/`;

  return AUTH_ENTRY_PATHS.has(normalized);
}

export function toInternalTarget(raw: string | null | undefined) {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || /[\\\u0000-\u001f\u007f]/.test(raw)) {
    return null;
  }

  return isAuthEntry(raw) ? null : normalizeInternalUrl(raw);
}

export function normalizeCustomRedirectUrl(raw: string): string {
  const trimmed = raw.trim();

  if (!trimmed) {
    return "";
  }

  if (/^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  return normalizeInternalUrl(
    trimmed.startsWith("/") ? trimmed : `/${trimmed}`,
  );
}

export function resolveLoginRedirectUrl(
  redirectAfterLogin: LoginRedirectMode,
  customRedirectUrl: string,
  options?: {
    next?: string | null;
    from?: string | null;
  },
) {
  const forced = toInternalTarget(options?.next);

  if (forced) {
    return forced;
  }

  switch (redirectAfterLogin) {
    case "member_center":
      return "/member/";
    case "current":
      return toInternalTarget(options?.from) ?? "/";
    case "custom":
      return normalizeCustomRedirectUrl(customRedirectUrl) || "/";
    case "home":
    default:
      return "/";
  }
}

export function buildMemberLoginHref(options?: {
  from?: string | null;
  next?: string | null;

  reauth?: boolean;
}) {
  const params = new URLSearchParams();
  const next = toInternalTarget(options?.next);
  const from = toInternalTarget(options?.from);

  if (next) {
    params.set("next", next);
  } else if (from) {
    params.set("from", from);
  }
  if (next && options?.reauth) {
    params.set("reauth", "1");
  }

  const query = params.toString();

  return query ? `${MEMBER_LOGIN_PATH}?${query}` : MEMBER_LOGIN_PATH;
}

export function buildMemberRegisterHref(options?: { next?: string | null; from?: string | null }) {
  const next = toInternalTarget(options?.next);
  const from = toInternalTarget(options?.from);
  const params = next ? new URLSearchParams({ next }) : from ? new URLSearchParams({ from }) : null;
  return params ? `${MEMBER_REGISTER_PATH}?${params}` : MEMBER_REGISTER_PATH;
}

export function buildMemberResetPasswordHref(options?: { next?: string | null; from?: string | null }) {
  const next = toInternalTarget(options?.next);
  const from = toInternalTarget(options?.from);
  const params = next ? new URLSearchParams({ next }) : from ? new URLSearchParams({ from }) : null;
  return params ? `${MEMBER_RESET_PASSWORD_PATH}?${params}` : MEMBER_RESET_PASSWORD_PATH;
}

export function isHardNavigationTarget(target: string): boolean {
  return /^https?:\/\//i.test(target) || target.startsWith("/oauth/");
}
